import {
  QueryClient,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { addMapperNotes } from "../../../apis";
import { SearchApiParams, Venue, VenuePinType } from "../../../types";
import { useAtom } from "jotai";
import { tagVenueSearchParamsAtom } from "../tagging";
import { Storage } from "common/storage";

export function usePostAddMapperNotes() {
  const accessToken = Storage.getCurrentUserToken();
  const queryClient = useQueryClient();
  const [tagVenueSearchParam] = useAtom(tagVenueSearchParamsAtom);

  const { mutate, error, status, data } = useMutation({
    mutationFn: ({
      venueId,
      businessReportIssueAction,
      note,
      returnOnDate,
      actionOnDate,
      locationDetails,
    }: {
      venueId: string;
      note: string;
      businessReportIssueAction: string;
      returnOnDate?: string;
      actionOnDate?: string;
      locationDetails?: Array<{ location_type: string; location_id: string }>;
    }) => {
      return addMapperNotes(
        venueId,
        note,
        businessReportIssueAction,
        accessToken || "",
        returnOnDate,
        actionOnDate,
        locationDetails,
      );
    },
    onSuccess: (_, { venueId, businessReportIssueAction }) => {
      queryClient.invalidateQueries({
        queryKey: ["loadVenueDetailsBySlug"],
        exact: false,
      });

      updateMapPin(
        venueId,
        queryClient,
        tagVenueSearchParam,
        true,
        businessReportIssueAction,
        accessToken || ""
      );
      updateMapPin(
        venueId,
        queryClient,
        tagVenueSearchParam,
        false,
        businessReportIssueAction,
        accessToken || ""
      );
    },
  });

  return { mutate, error, status, data };
}

function updateMapPin(
  venueId: string,
  queryClient: QueryClient,
  venueSearchParams: SearchApiParams,
  isHome: boolean,
  businessReportIssueAction: string,
  accessToken: string
) {
  const data = queryClient.getQueryData([
    isHome ? "homePins" : "tagPins",
    venueSearchParams,
    accessToken,
  ]);
  if (!data) {
    return;
  }
  const { locations, accessibility_tags } = data as {
    locations: Venue[];
    accessibility_tags: any[];
  };
  const updatedLocation = locations.find(
    (l) => l.business.id.toString() === venueId
  );
  if (updatedLocation) {
    if (
      businessReportIssueAction === "1" ||
      businessReportIssueAction === "2" ||
      businessReportIssueAction === "3"
    ) {
      updatedLocation.isReturnToVenue = 0;
      updatedLocation.hasNotesForMappers = true;
      updatedLocation.isNoteStatusPending = 1;
      queryClient.setQueryData(
        [isHome ? "homePins" : "tagPins", venueSearchParams, accessToken],
        {
          locations: [
            ...locations.filter((e) => e.business.id.toString() !== venueId.toString()),
            updatedLocation,
          ],
          accessibility_tags,
        }
      );
    } else if (businessReportIssueAction === "5") {
      updatedLocation.isNoteStatusPending = 1;
      updatedLocation.hasNotesForMappers = true;
      //remove this location from the list
      queryClient.setQueryData(
        [isHome ? "homePins" : "tagPins", venueSearchParams, accessToken],
        {
          locations: locations.filter(
            (e) => e.business.id.toString() !== venueId.toString()
          ),
          accessibility_tags,
        }
      );
    } else if (businessReportIssueAction === "8") {
      updatedLocation.isReturnToVenue = 1;
      updatedLocation.hasNotesForMappers = true;
      queryClient.setQueryData(
        [isHome ? "homePins" : "tagPins", venueSearchParams, accessToken],
        {
          locations: [
            ...locations.filter((e) => e.business.id.toString() !== venueId.toString()),
            updatedLocation,
          ],
          accessibility_tags,
        }
      );
    } else {
      updatedLocation.hasNotesForMappers = true;
      queryClient.setQueryData(
        [isHome ? "homePins" : "tagPins", venueSearchParams, accessToken],
        {
          locations: [
            ...locations.filter((e) => e.business.id.toString() !== venueId.toString()),
            updatedLocation,
          ],
          accessibility_tags,
        }
      );
    }
  }
}
